import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { Fragment, ReactElement, Suspense } from 'react';
import { getButtonStyles } from './Button';
import { FullLoader } from './Loader';
import { useSuspenseQuery } from '@apollo/client/react';
import { GQL_FRAGMENT_PROJECT_QUERY_LIST, GQL_QUERY_INDEX_QUERIES } from '../../queries';
import { getFragmentData } from '@sc/graphql';
import { useTheme } from '../providers/ThemeContext';
import classNames from 'classnames';

interface Props {
  projectUuid: string;
  navigateToQuery: (chatUuid: string) => void;
}

const HistoryTab = ({
  isBookmarked,
  projectUuid,
  navigateToQuery,
}: Props & {
  isBookmarked?: boolean;
}) => {
  const path = window.location.pathname;
  const { inverted } = useTheme();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_QUERIES, {
    variables: {
      input: {
        filter: {
          isBookmarked,
          projectUuid,
        },
        pagination: {
          page: 1,
          perPage: 100,
        },
      },
    },
    context: {
      isBatched: true
    }
  });

  return (
      <ul className="flex flex-col overflow-y-auto overscroll-contain space-y-2 px-2 pt-2 w-full pb-5">
        {data.indexQueries.items.map(i => getFragmentData(GQL_FRAGMENT_PROJECT_QUERY_LIST, i)).map((i) => (
          <li className='w-full'>
            <button
              {...getButtonStyles({
                size: 'small',
                type: 'simple',
                additionalClassnames: classNames(
                  'cursor-pointer w-full',
                  inverted
                    ? '!text-[#252525] hover:!bg-black-t-900 data-[active=true]:!bg-black-t-900'
                    : 'hover:text-white data-[active=true]:bg-grey-t-800'
                ),
              })}
              key={i.uuid}
              type="button"
              data-active={path.includes(i.uuid)}
              onClick={() => navigateToQuery(i.uuid)}
            >
              <span className="text-left w-full truncate">{i.title}</span>
            </button>
          </li>
        ))}
      </ul>
  );
};

export const ChatHistory = ({
  projectUuid,
  navigateToQuery,
}: Props): ReactElement => {
  const { inverted, theme } = useTheme();

  const tabClassnames = classNames(
    '!rounded-none relative group outline-none',
    inverted
      ? '!text-grey-500 data-selected:!text-black hover:!bg-transparent'
      : 'data-selected:text-white'
  );

  const underlineStyle =
    inverted && theme?.brandColour
      ? { backgroundColor: theme.brandColour }
      : undefined;

  return (
    <TabGroup
      className={classNames(
        'overflow-hidden flex flex-col w-full sm:w-90',
        inverted && 'bg-white text-black'
      )}
    >
      <TabList
        className={classNames(
          'flex pt-1 px-5 justify-around border-b',
          inverted ? 'border-black-t-700' : 'border-grey-600'
        )}
      >
        <Tab
          {...getButtonStyles({
            type: 'simple',
            size: 'medium',
            additionalClassnames: tabClassnames,
          })}
        >
          <span>Chats</span>
          <hr
            className={classNames(
              'absolute group-data-selected:opacity-100 opacity-0 bottom-0 h-0.5 border-none rounded w-full transition-all duration-150',
              !underlineStyle && 'bg-primary-400'
            )}
            style={underlineStyle}
          />
        </Tab>
        <Tab
          {...getButtonStyles({
            type: 'simple',
            size: 'medium',
            additionalClassnames: tabClassnames,
          })}
        >
          <span>Bookmarks</span>
          <hr
            className={classNames(
              'absolute group-data-selected:opacity-100 opacity-0 bottom-0 h-0.5 border-none rounded w-full transition-all duration-150',
              !underlineStyle && 'bg-primary-400'
            )}
            style={underlineStyle}
          />
        </Tab>
      </TabList>
      <TabPanels as={Fragment}>
        <TabPanel className="flex overflow-hidden">
          <Suspense fallback={<FullLoader className='size-5' />}>
            <HistoryTab
              navigateToQuery={navigateToQuery}
              projectUuid={projectUuid}
            />
          </Suspense>
        </TabPanel>
        <TabPanel>
          <Suspense fallback={<FullLoader className='size-5' />}>
            <HistoryTab
              navigateToQuery={navigateToQuery}
              projectUuid={projectUuid}
              isBookmarked
            />
          </Suspense>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
};
